// ClientBots.jsx - Client Portal Bot Control Panel
// Matches main dashboard BotsTab but without VM cloning and error logs

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useClientAuth } from '../../context/ClientAuthContext'
import { useAutoRefresh } from '../../hooks/useAutoRefresh'
import {
    Bot, RefreshCw, Play, Square, RotateCcw, BarChart3,
    Activity, Clock, WifiOff, ChevronDown, ChevronUp,
    Phone, PhoneForwarded, Voicemail, PhoneOff, PhoneMissed,
    UserX, Ban, CheckSquare, Square as SquareIcon, AlertTriangle,
    Server, X, Check, Power, PowerOff, ToggleLeft, ToggleRight, RefreshCcw,
    Settings, Mic, Loader2, Volume2, Pause, AlertCircle, User, Users
} from 'lucide-react'
import '../../components/BotsTab.css'

// Date presets
const DATE_PRESETS = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: '7days', label: '7 Days' },
    { key: '30days', label: '30 Days' },
    { key: 'custom', label: 'Custom' }
]

// Status configuration
const STATUS_CONFIG = {
    active: { label: 'Active', color: '#10b981', icon: Activity },
    waiting: { label: 'Logged Out', color: '#f59e0b', icon: Clock },
    offline: { label: 'Offline', color: '#6b7280', icon: WifiOff },
    running: { label: 'Active', color: '#10b981', icon: Activity },
    dial_level_0: { label: 'Dial Lvl 0', color: '#ef4444', icon: AlertTriangle },
    login_retry: { label: 'Login Retry', color: '#f59e0b', icon: AlertTriangle },
    login_error: { label: 'Login Error', color: '#ef4444', icon: X }
}

const formatTimeAgo = (date) => {
    if (!date) return '-'
    const diff = Math.abs(Math.floor((new Date() - new Date(date)) / 1000))
    if (diff < 60) return `${diff}s`
    if (diff < 3600) return `${Math.floor(diff / 60)}m`
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`
    return `${Math.floor(diff / 86400)}d`
}

const getDateString = (date) => date.toISOString().split('T')[0]

const getStatusConfig = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.offline

// Toast notification component
function Toast({ message, type, onClose }) {
    useEffect(() => {
        const timer = setTimeout(onClose, 4000)
        return () => clearTimeout(timer)
    }, [onClose])

    return (
        <div className={`bt-toast bt-toast-${type}`}>
            {type === 'success' && <Check size={16} />}
            {type === 'error' && <X size={16} />}
            {type === 'info' && <Server size={16} />}
            <span>{message}</span>
            <button onClick={onClose}><X size={14} /></button>
        </div>
    )
}

// Confirmation dialog component with checkbox support
function ConfirmDialog({ title, message, onConfirm, onCancel, checkboxes: initialCheckboxes = [], confirmText = "Yes, Continue" }) {
    const [checkboxStates, setCheckboxStates] = useState(
        initialCheckboxes.map(cb => cb.checked ?? false)
    )

    const handleCheckboxChange = (idx, checked) => {
        setCheckboxStates(prev => {
            const next = [...prev]
            next[idx] = checked
            return next
        })
    }

    const handleConfirm = () => {
        onConfirm(checkboxStates)
    }

    return (
        <div className="bt-confirm-overlay" onClick={onCancel}>
            <div className="bt-confirm-dialog" onClick={e => e.stopPropagation()}>
                <div className="bt-confirm-title">{title}</div>
                <div className="bt-confirm-message">{message}</div>
                {initialCheckboxes.length > 0 && (
                    <div className="bt-confirm-checkboxes">
                        {initialCheckboxes.map((cb, idx) => (
                            <label key={idx} className="bt-confirm-checkbox">
                                <input
                                    type="checkbox"
                                    checked={checkboxStates[idx]}
                                    onChange={(e) => handleCheckboxChange(idx, e.target.checked)}
                                />
                                <span>{cb.label}</span>
                            </label>
                        ))}
                    </div>
                )}
                <div className="bt-confirm-actions">
                    <button className="bt-confirm-no" onClick={onCancel}>Cancel</button>
                    <button className="bt-confirm-yes" onClick={handleConfirm}>{confirmText}</button>
                </div>
            </div>
        </div>
    )
}

export default function ClientBots() {
    const { clientApi } = useClientAuth()
    const [bots, setBots] = useState([])
    const [loading, setLoading] = useState(true)
    const [commandState, setCommandState] = useState({})

    // Date selection
    const [datePreset, setDatePreset] = useState('today')
    const [customFrom, setCustomFrom] = useState(getDateString(new Date()))
    const [customTo, setCustomTo] = useState(getDateString(new Date()))
    const [showDatePicker, setShowDatePicker] = useState(false)

    // Bulk selection
    const [selectedBots, setSelectedBots] = useState(new Set())
    const [bulkLoading, setBulkLoading] = useState(false)
    const [showRangeModal, setShowRangeModal] = useState(false)
    const [rangeFrom, setRangeFrom] = useState('')
    const [rangeTo, setRangeTo] = useState('')

    // Expanded VM row
    const [expandedBot, setExpandedBot] = useState(null)
    const [vmStatus, setVmStatus] = useState({})

    // Avatar switching
    const [clientAvatars, setClientAvatars] = useState([])
    const [showAvatarModal, setShowAvatarModal] = useState(false)
    const [avatarSwitching, setAvatarSwitching] = useState(false)

    // Toast notifications
    const [toasts, setToasts] = useState([])

    // Confirmation dialog
    const [confirmAction, setConfirmAction] = useState(null)

    // Status filter
    const [selectedStatus, setSelectedStatus] = useState('')

    // Dialer filter (when multiple linked dialers share SIP numbers)
    const [selectedDialer, setSelectedDialer] = useState('')

    // Edit modal state
    const [showEditModal, setShowEditModal] = useState(false)
    const [editBot, setEditBot] = useState(null)
    const [editBots, setEditBots] = useState([]) // For bulk edit
    const [bulkEditMode, setBulkEditMode] = useState(false)
    const [editLoading, setEditLoading] = useState(false)
    const [editSaving, setEditSaving] = useState(false)
    const [voiceSets, setVoiceSets] = useState([])
    const [licenseData, setLicenseData] = useState({
        voice_set_pool: []
    })

    // Audio preview state
    const [playingVoiceSet, setPlayingVoiceSet] = useState(null)
    const [audioLoading, setAudioLoading] = useState(null)
    const audioRef = React.useRef(new Audio())

    // Get date range based on preset
    const getDateRange = useCallback(() => {
        const today = new Date()
        switch (datePreset) {
            case 'today':
                return { from: getDateString(today), to: getDateString(today) }
            case 'yesterday':
                const yesterday = new Date(today)
                yesterday.setDate(yesterday.getDate() - 1)
                return { from: getDateString(yesterday), to: getDateString(yesterday) }
            case '7days':
                const week = new Date(today)
                week.setDate(week.getDate() - 7)
                return { from: getDateString(week), to: getDateString(today) }
            case '30days':
                const month = new Date(today)
                month.setDate(month.getDate() - 30)
                return { from: getDateString(month), to: getDateString(today) }
            case 'custom':
                return { from: customFrom, to: customTo }
            default:
                return { from: getDateString(today), to: getDateString(today) }
        }
    }, [datePreset, customFrom, customTo])

    // Show toast
    const showToast = useCallback((message, type = 'info') => {
        const id = Date.now()
        setToasts(prev => [...prev, { id, message, type }])
    }, [])

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id))
    }, [])

    // Load assigned avatars
    const loadAvatars = useCallback(async () => {
        try {
            const data = await clientApi('/avatars')
            if (data.success) setClientAvatars(data.avatars || [])
        } catch (e) { console.error('Failed to load avatars:', e) }
    }, [clientApi])

    useEffect(() => { loadAvatars() }, [loadAvatars])

    // Fetch bots with dispositions
    const fetchBots = useCallback(async (silent = false) => {
        if (!silent) setLoading(true)
        try {
            const dateRange = getDateRange()
            const data = await clientApi(`/bots?from=${dateRange.from}&to=${dateRange.to}`)
            if (data.success) {
                // Preserve UI state (expanded rows, selections) when updating
                setBots(prevBots => {
                    return data.bots.map(newBot => {
                        const existingBot = prevBots.find(b => b.sip_id === newBot.sip_id && b.dialer_id === newBot.dialer_id)
                        return existingBot ? { ...newBot } : newBot
                    })
                })
            }
        } catch (error) {
            if (!silent) showToast('Failed to load bots', 'error')
        }
        if (!silent) setLoading(false)
    }, [clientApi, showToast, getDateRange])

    // Switch avatar for selected bots
    const switchAvatar = useCallback(async (avatarId) => {
        if (selectedBots.size === 0) { showToast('Select bots first', 'error'); return }
        setAvatarSwitching(true)
        try {
            const data = await clientApi('/bots/switch-avatar', {
                method: 'POST',
                body: JSON.stringify({ sip_ids: [...selectedBots], avatar_id: avatarId, dialer_id: selectedDialer || undefined })
            })
            if (data.success) {
                showToast(`Avatar switched for ${data.updated_count} bots`)
                setShowAvatarModal(false)
                fetchBots(true)
            } else {
                showToast(data.error || 'Failed to switch avatar', 'error')
            }
        } catch (e) { showToast('Failed to switch avatar', 'error') }
        setAvatarSwitching(false)
    }, [selectedBots, clientApi, showToast, fetchBots])

    // Silent fetch for auto-refresh (doesn't show loading state)
    const silentFetch = useCallback(async () => {
        await fetchBots(true)
    }, [fetchBots])

    // Auto-refresh bots every 10 seconds
    useAutoRefresh(silentFetch, 10000, true)

    useEffect(() => {
        fetchBots()
    }, [fetchBots])

    // List of linked dialers (derived from the bots) for the dialer filter dropdown
    const dialerOptions = useMemo(() => {
        const m = new Map()
        bots.forEach(b => {
            if (b.dialer_id != null && !m.has(b.dialer_id)) {
                m.set(b.dialer_id, b.dialer_name || `Dialer ${b.dialer_id}`)
            }
        })
        return Array.from(m, ([id, name]) => ({ id, name }))
            .sort((a, b) => String(a.name).localeCompare(String(b.name)))
    }, [bots])

    // Bots after the dialer filter (base for stats, totals and the table).
    // When two dialers share SIP numbers, picking a dialer isolates that one.
    const dialerFilteredBots = useMemo(() => {
        if (!selectedDialer) return bots
        return bots.filter(b => String(b.dialer_id) === String(selectedDialer))
    }, [bots, selectedDialer])

    // Resolve which dialer a command should target. The active dialer filter wins
    // (the client explicitly chose it); otherwise fall back to the bot's own dialer.
    // Keeps every action scoped to ONE dialer even when two share SIP numbers.
    const resolveDialerId = useCallback((sipId) => {
        if (selectedDialer) return selectedDialer
        const b = bots.find(x => String(x.sip_id) === String(sipId))
        return b ? b.dialer_id : undefined
    }, [selectedDialer, bots])

    // Status stats
    const statusStats = useMemo(() => {
        const stats = { active: 0, waiting: 0, offline: 0, dial_level_0: 0 }
        dialerFilteredBots.forEach(bot => {
            const status = bot.effective_status || 'offline'
            if (status === 'active' || status === 'running') stats.active++
            else if (status === 'waiting') stats.waiting++
            else if (status === 'dial_level_0') stats.dial_level_0++
            else stats.offline++
        })
        return stats
    }, [dialerFilteredBots])

    // Call totals (ensure numeric addition, not string concatenation)
    const totals = useMemo(() => {
        return dialerFilteredBots.reduce((acc, bot) => {
            acc.calls += Number(bot.calls) || 0
            acc.xfer += Number(bot.xfer) || 0
            acc.am += Number(bot.am) || 0
            acc.ni += Number(bot.ni) || 0
            acc.na += Number(bot.na) || 0
            acc.dnc += Number(bot.dnc) || 0
            acc.dnq += Number(bot.dnq) || 0
            return acc
        }, { calls: 0, xfer: 0, am: 0, ni: 0, na: 0, dnc: 0, dnq: 0 })
    }, [dialerFilteredBots])

    // Filtered bots
    const filteredBots = useMemo(() => {
        let filtered = dialerFilteredBots
        if (selectedStatus) {
            filtered = dialerFilteredBots.filter(bot => {
                const status = bot.effective_status || 'offline'
                if (selectedStatus === 'active') return status === 'active' || status === 'running'
                return status === selectedStatus
            })
        }
        return [...filtered].sort((a, b) => {
            const extractNum = (s) => { const m = String(s).match(/(\d+)/); return m ? parseInt(m[1]) : null; }
            const aNum = extractNum(a.sip_id)
            const bNum = extractNum(b.sip_id)
            if (aNum !== null && bNum !== null) return aNum - bNum
            return String(a.sip_id).localeCompare(String(b.sip_id))
        })
    }, [dialerFilteredBots, selectedStatus])

    // Execute bot command directly (helper)
    const executeCommand = async (sipId, command) => {
        setCommandState(prev => ({ ...prev, [sipId]: { cmd: command, status: command === 'start' ? 'Logging in...' : 'Logging out...' } }))

        try {
            await clientApi(`/bots/${sipId}/command`, {
                method: 'POST',
                body: JSON.stringify({ command, dialer_id: resolveDialerId(sipId) })
            })
            showToast(`${command === 'start' ? 'Start' : 'Stop'} command sent to ${sipId}`, 'success')
            setTimeout(fetchBots, 2000)
        } catch (error) {
            showToast(`Failed to send ${command} command`, 'error')
            setCommandState(prev => ({ ...prev, [sipId]: { ...prev[sipId], error: true, status: 'Failed' } }))
            return
        }

        setTimeout(() => {
            setCommandState(prev => { const n = { ...prev }; delete n[sipId]; return n })
        }, 3000)
    }

    // Send bot command with confirmation for auto-login/revive toggle
    const sendCommand = async (sipId, command) => {
        const bot = bots.find(b => b.sip_id === sipId)
        if (!bot) {
            executeCommand(sipId, command)
            return
        }

        // For STOP command: check if auto_login or auto_revive is enabled
        if (command === 'stop') {
            const hasAutoLogin = bot.auto_login === 1 || bot.auto_login === true
            const hasAutoRevive = bot.auto_revive === 1 || bot.auto_revive === true

            if (hasAutoLogin || hasAutoRevive) {
                const checkboxes = []
                if (hasAutoLogin) {
                    checkboxes.push({ label: 'Disable Auto-Login', checked: false })
                }
                if (hasAutoRevive) {
                    checkboxes.push({ label: 'Disable Auto-Revive', checked: false })
                }

                setConfirmAction({
                    title: 'Stop Bot?',
                    message: `Bot ${sipId} has auto features enabled. Also disable them?`,
                    checkboxes,
                    confirmText: 'Stop Bot',
                    onConfirm: async (checkboxStates) => {
                        setConfirmAction(null)

                        // Disable auto features if checked
                        let checkboxIdx = 0
                        if (hasAutoLogin && checkboxStates[checkboxIdx]) {
                            try {
                                await clientApi(`/bots/${sipId}/auto-login`, {
                                    method: 'POST',
                                    body: JSON.stringify({ enabled: false, dialer_id: bot.dialer_id })
                                })
                                setBots(prev => prev.map(b => b.sip_id === sipId ? { ...b, auto_login: 0 } : b))
                            } catch (err) { console.error('Failed to disable auto-login:', err) }
                            checkboxIdx++
                        }
                        if (hasAutoRevive && checkboxStates[checkboxIdx]) {
                            try {
                                await clientApi(`/bots/${sipId}/auto-revive`, {
                                    method: 'POST',
                                    body: JSON.stringify({ enabled: false, dialer_id: bot.dialer_id })
                                })
                                setBots(prev => prev.map(b => b.sip_id === sipId ? { ...b, auto_revive: 0 } : b))
                            } catch (err) { console.error('Failed to disable auto-revive:', err) }
                        }

                        executeCommand(sipId, command)
                    }
                })
                return
            }
        }

        // For START command: check if auto_login is disabled
        if (command === 'start') {
            const hasAutoLogin = bot.auto_login === 1 || bot.auto_login === true

            if (!hasAutoLogin) {
                setConfirmAction({
                    title: 'Start Bot?',
                    message: `Bot ${sipId} does not have Auto-Login enabled. Would you like to enable it?`,
                    checkboxes: [
                        { label: 'Enable Auto-Login', checked: false },
                        { label: 'Enable Auto-Revive', checked: false }
                    ],
                    confirmText: 'Start Bot',
                    onConfirm: async (checkboxStates) => {
                        setConfirmAction(null)

                        // Enable auto features if checked
                        if (checkboxStates[0]) {
                            try {
                                await clientApi(`/bots/${sipId}/auto-login`, {
                                    method: 'POST',
                                    body: JSON.stringify({ enabled: true, dialer_id: bot.dialer_id })
                                })
                                setBots(prev => prev.map(b => b.sip_id === sipId ? { ...b, auto_login: 1 } : b))
                            } catch (err) { console.error('Failed to enable auto-login:', err) }
                        }
                        if (checkboxStates[1] && checkboxStates[0]) {
                            try {
                                await clientApi(`/bots/${sipId}/auto-revive`, {
                                    method: 'POST',
                                    body: JSON.stringify({ enabled: true, dialer_id: bot.dialer_id })
                                })
                                setBots(prev => prev.map(b => b.sip_id === sipId ? { ...b, auto_revive: 1 } : b))
                            } catch (err) { console.error('Failed to enable auto-revive:', err) }
                        }

                        executeCommand(sipId, command)
                    }
                })
                return
            }
        }

        // No confirmation needed
        executeCommand(sipId, command)
    }

    // Toggle auto-login
    const toggleAutoLogin = async (bot) => {
        const newValue = !bot.auto_login
        setBots(prev => prev.map(b => b.sip_id === bot.sip_id ? { ...b, auto_login: newValue ? 1 : 0 } : b))

        try {
            await clientApi(`/bots/${bot.sip_id}/auto-login`, {
                method: 'POST',
                body: JSON.stringify({ enabled: newValue, dialer_id: bot.dialer_id })
            })
            showToast(`Auto-login ${newValue ? 'enabled' : 'disabled'} for ${bot.sip_id}`, 'success')
        } catch (err) {
            setBots(prev => prev.map(b => b.sip_id === bot.sip_id ? { ...b, auto_login: bot.auto_login } : b))
            showToast('Failed to update auto-login', 'error')
        }
    }

    // Toggle auto-revive
    const toggleAutoRevive = async (bot) => {
        if (!bot.auto_login) {
            showToast('Enable Auto-Login first before enabling Auto-Revive', 'error')
            return
        }
        const newValue = !bot.auto_revive
        setBots(prev => prev.map(b => b.sip_id === bot.sip_id ? { ...b, auto_revive: newValue ? 1 : 0, auto_revive_count: 0 } : b))

        try {
            await clientApi(`/bots/${bot.sip_id}/auto-revive`, {
                method: 'POST',
                body: JSON.stringify({ enabled: newValue, dialer_id: bot.dialer_id })
            })
            showToast(`Auto-revive ${newValue ? 'enabled' : 'disabled'} for ${bot.sip_id}`, 'success')
        } catch (err) {
            setBots(prev => prev.map(b => b.sip_id === bot.sip_id ? { ...b, auto_revive: bot.auto_revive } : b))
            showToast('Failed to update auto-revive', 'error')
        }
    }

    // Fetch VM status
    const fetchVMStatus = async (bot) => {
        if (!bot.vmid || !bot.server_ip) return

        setVmStatus(prev => ({ ...prev, [bot.sip_id]: { loading: true } }))

        try {
            const data = await clientApi(`/vm/status/${bot.sip_id}`)
            setVmStatus(prev => ({ ...prev, [bot.sip_id]: { status: data.status, loading: false } }))
        } catch (err) {
            setVmStatus(prev => ({ ...prev, [bot.sip_id]: { status: 'unknown', loading: false } }))
        }
    }

    // Toggle expand
    const toggleExpand = (bot) => {
        if (expandedBot === bot.sip_id) {
            setExpandedBot(null)
        } else {
            setExpandedBot(bot.sip_id)
            fetchVMStatus(bot)
        }
    }

    // Execute VM action directly (helper)
    const executeVMAction = async (action, bot) => {
        setVmStatus(prev => ({ ...prev, [bot.sip_id]: { ...prev[bot.sip_id], loading: true } }))

        try {
            await clientApi('/vm/action', {
                method: 'POST',
                body: JSON.stringify({ sip_id: bot.sip_id, action, dialer_id: bot.dialer_id })
            })
            showToast(`VM ${action} command sent`, 'success')
            setTimeout(() => fetchVMStatus(bot), 5000)
        } catch (err) {
            showToast(`Failed to ${action} VM: ${err.message}`, 'error')
        }
    }

    // VM action with confirmation for auto-login/revive toggle
    const vmAction = (action, bot) => {
        const hasAutoLogin = bot.auto_login === 1 || bot.auto_login === true
        const hasAutoRevive = bot.auto_revive === 1 || bot.auto_revive === true

        // For STOP or RESTART: offer to disable auto features
        if ((action === 'stop' || action === 'restart') && (hasAutoLogin || hasAutoRevive)) {
            const checkboxes = []
            if (hasAutoLogin) {
                checkboxes.push({ label: 'Disable Auto-Login', checked: false })
            }
            if (hasAutoRevive) {
                checkboxes.push({ label: 'Disable Auto-Revive', checked: false })
            }

            setConfirmAction({
                title: `${action.charAt(0).toUpperCase() + action.slice(1)} VM?`,
                message: `Are you sure you want to ${action} VM ${bot.vmid}? Also disable auto features?`,
                checkboxes,
                confirmText: `${action.charAt(0).toUpperCase() + action.slice(1)} VM`,
                onConfirm: async (checkboxStates) => {
                    setConfirmAction(null)

                    // Disable auto features if checked
                    let checkboxIdx = 0
                    if (hasAutoLogin && checkboxStates[checkboxIdx]) {
                        try {
                            await clientApi(`/bots/${bot.sip_id}/auto-login`, {
                                method: 'POST',
                                body: JSON.stringify({ enabled: false, dialer_id: bot.dialer_id })
                            })
                            setBots(prev => prev.map(b => b.sip_id === bot.sip_id ? { ...b, auto_login: 0 } : b))
                        } catch (err) { console.error('Failed to disable auto-login:', err) }
                        checkboxIdx++
                    }
                    if (hasAutoRevive && checkboxStates[checkboxIdx]) {
                        try {
                            await clientApi(`/bots/${bot.sip_id}/auto-revive`, {
                                method: 'POST',
                                body: JSON.stringify({ enabled: false, dialer_id: bot.dialer_id })
                            })
                            setBots(prev => prev.map(b => b.sip_id === bot.sip_id ? { ...b, auto_revive: 0 } : b))
                        } catch (err) { console.error('Failed to disable auto-revive:', err) }
                    }

                    executeVMAction(action, bot)
                }
            })
            return
        }

        // For START: offer to enable auto features if not enabled
        if (action === 'start' && !hasAutoLogin) {
            setConfirmAction({
                title: 'Start VM?',
                message: `Are you sure you want to start VM ${bot.vmid}? Bot does not have Auto-Login enabled.`,
                checkboxes: [
                    { label: 'Enable Auto-Login', checked: false },
                    { label: 'Enable Auto-Revive', checked: false }
                ],
                confirmText: 'Start VM',
                onConfirm: async (checkboxStates) => {
                    setConfirmAction(null)

                    // Enable auto features if checked
                    if (checkboxStates[0]) {
                        try {
                            await clientApi(`/bots/${bot.sip_id}/auto-login`, {
                                method: 'POST',
                                body: JSON.stringify({ enabled: true, dialer_id: bot.dialer_id })
                            })
                            setBots(prev => prev.map(b => b.sip_id === bot.sip_id ? { ...b, auto_login: 1 } : b))
                        } catch (err) { console.error('Failed to enable auto-login:', err) }
                    }
                    if (checkboxStates[1] && checkboxStates[0]) {
                        try {
                            await clientApi(`/bots/${bot.sip_id}/auto-revive`, {
                                method: 'POST',
                                body: JSON.stringify({ enabled: true, dialer_id: bot.dialer_id })
                            })
                            setBots(prev => prev.map(b => b.sip_id === bot.sip_id ? { ...b, auto_revive: 1 } : b))
                        } catch (err) { console.error('Failed to enable auto-revive:', err) }
                    }

                    executeVMAction(action, bot)
                }
            })
            return
        }

        // Default confirmation without checkboxes
        setConfirmAction({
            title: `${action.charAt(0).toUpperCase() + action.slice(1)} VM?`,
            message: `Are you sure you want to ${action} VM ${bot.vmid}?${action === 'restart' ? ' Bot will auto-login if Auto-Login is enabled.' : ''}`,
            confirmText: `${action.charAt(0).toUpperCase() + action.slice(1)} VM`,
            onConfirm: async () => {
                setConfirmAction(null)
                executeVMAction(action, bot)
            }
        })
    }

    // Bulk selection
    const toggleSelect = (sipId) => {
        setSelectedBots(prev => {
            const next = new Set(prev)
            if (next.has(sipId)) next.delete(sipId)
            else next.add(sipId)
            return next
        })
    }

    const toggleSelectAll = () => {
        if (selectedBots.size === filteredBots.length) {
            setSelectedBots(new Set())
        } else {
            setSelectedBots(new Set(filteredBots.map(b => b.sip_id)))
        }
    }

    const selectRange = () => {
        const parseId = (val) => {
            const match = String(val).match(/^([a-zA-Z]*)(\d+)$/)
            if (!match) return null
            return { prefix: match[1], num: parseInt(match[2]) }
        }
        const fromParsed = parseId(rangeFrom)
        const toParsed = parseId(rangeTo)
        if (!fromParsed || !toParsed || fromParsed.num > toParsed.num) {
            showToast('Invalid range. Use e.g. 8026-8030 or Agent1-Agent20', 'error')
            return
        }
        const prefix = fromParsed.prefix || toParsed.prefix || ''
        const matching = filteredBots.filter(b => {
            const parsed = parseId(b.sip_id)
            if (!parsed) return false
            if (prefix && parsed.prefix.toLowerCase() !== prefix.toLowerCase()) return false
            return parsed.num >= fromParsed.num && parsed.num <= toParsed.num
        })
        if (matching.length === 0) {
            showToast('No bots found in that range', 'error')
            return
        }
        setSelectedBots(prev => {
            const next = new Set(prev)
            matching.forEach(b => next.add(b.sip_id))
            return next
        })
        setShowRangeModal(false)
        setRangeFrom('')
        setRangeTo('')
        showToast(`Selected ${matching.length} bots (${rangeFrom}-${rangeTo})`)
    }

    // Execute bulk command directly (helper)
    const executeBulkCommand = async (command, sipIds) => {
        setBulkLoading(true)

        // Set initial "Sending..." status for all bots
        setCommandState(prev => {
            const next = { ...prev }
            sipIds.forEach(sipId => { next[sipId] = { cmd: command, status: 'Sending...' } })
            return next
        })

        for (const sipId of sipIds) {
            try {
                await clientApi(`/bots/${sipId}/command`, {
                    method: 'POST',
                    body: JSON.stringify({ command, dialer_id: resolveDialerId(sipId) })
                })
            } catch (err) {
                console.error(`Failed to ${command} bot ${sipId}:`, err)
            }
        }

        showToast(`${command === 'stop' ? 'Logout' : 'Login'} command sent to ${sipIds.length} bots`, 'success')
        setBulkLoading(false)
        setSelectedBots(new Set())

        // Clear "Sending..." status after 15 seconds - actual status will show from bot refresh
        setTimeout(() => {
            setCommandState(prev => {
                const next = { ...prev }
                sipIds.forEach(sipId => {
                    if (next[sipId]?.status === 'Sending...') {
                        delete next[sipId]
                    }
                })
                return next
            })
        }, 15000)

        setTimeout(fetchBots, 2000)
    }

    // Bulk command with confirmation for auto-toggle
    const bulkCommand = async (command) => {
        const sipIds = [...selectedBots]
        if (sipIds.length === 0) return

        const selectedBotData = bots.filter(b => sipIds.includes(b.sip_id))

        // For STOP command: check which bots have auto features enabled
        if (command === 'stop') {
            const botsWithAutoLogin = selectedBotData.filter(b => b.auto_login === 1 || b.auto_login === true)
            const botsWithAutoRevive = selectedBotData.filter(b => b.auto_revive === 1 || b.auto_revive === true)

            if (botsWithAutoLogin.length > 0 || botsWithAutoRevive.length > 0) {
                const checkboxes = []
                if (botsWithAutoLogin.length > 0) {
                    checkboxes.push({ label: `Disable Auto-Login (${botsWithAutoLogin.length} bots)`, checked: false })
                }
                if (botsWithAutoRevive.length > 0) {
                    checkboxes.push({ label: `Disable Auto-Revive (${botsWithAutoRevive.length} bots)`, checked: false })
                }

                setConfirmAction({
                    title: `Stop ${sipIds.length} Bots?`,
                    message: `Some bots have auto features enabled. Also disable them?`,
                    checkboxes,
                    confirmText: 'Stop Bots',
                    onConfirm: async (checkboxStates) => {
                        setConfirmAction(null)

                        // Disable auto features if checked
                        let checkboxIdx = 0
                        if (botsWithAutoLogin.length > 0 && checkboxStates[checkboxIdx]) {
                            for (const bot of botsWithAutoLogin) {
                                try {
                                    await clientApi(`/bots/${bot.sip_id}/auto-login`, {
                                        method: 'POST',
                                        body: JSON.stringify({ enabled: false, dialer_id: bot.dialer_id })
                                    })
                                } catch (err) { console.error(`Failed to disable auto-login for ${bot.sip_id}:`, err) }
                            }
                            setBots(prev => prev.map(b =>
                                botsWithAutoLogin.some(ab => ab.sip_id === b.sip_id) ? { ...b, auto_login: 0 } : b
                            ))
                            checkboxIdx++
                        }
                        if (botsWithAutoRevive.length > 0 && checkboxStates[checkboxIdx]) {
                            for (const bot of botsWithAutoRevive) {
                                try {
                                    await clientApi(`/bots/${bot.sip_id}/auto-revive`, {
                                        method: 'POST',
                                        body: JSON.stringify({ enabled: false, dialer_id: bot.dialer_id })
                                    })
                                } catch (err) { console.error(`Failed to disable auto-revive for ${bot.sip_id}:`, err) }
                            }
                            setBots(prev => prev.map(b =>
                                botsWithAutoRevive.some(ab => ab.sip_id === b.sip_id) ? { ...b, auto_revive: 0 } : b
                            ))
                        }

                        executeBulkCommand(command, sipIds)
                    }
                })
                return
            }
        }

        // For START command: check which bots don't have auto_login
        if (command === 'start') {
            const botsWithoutAutoLogin = selectedBotData.filter(b => !b.auto_login || b.auto_login === 0)

            if (botsWithoutAutoLogin.length > 0) {
                setConfirmAction({
                    title: `Start ${sipIds.length} Bots?`,
                    message: `${botsWithoutAutoLogin.length} bot(s) don't have Auto-Login enabled. Would you like to enable it?`,
                    checkboxes: [
                        { label: `Enable Auto-Login (${botsWithoutAutoLogin.length} bots)`, checked: false },
                        { label: `Enable Auto-Revive (${botsWithoutAutoLogin.length} bots)`, checked: false }
                    ],
                    confirmText: 'Start Bots',
                    onConfirm: async (checkboxStates) => {
                        setConfirmAction(null)

                        // Enable auto features if checked
                        if (checkboxStates[0]) {
                            for (const bot of botsWithoutAutoLogin) {
                                try {
                                    await clientApi(`/bots/${bot.sip_id}/auto-login`, {
                                        method: 'POST',
                                        body: JSON.stringify({ enabled: true, dialer_id: bot.dialer_id })
                                    })
                                } catch (err) { console.error(`Failed to enable auto-login for ${bot.sip_id}:`, err) }
                            }
                            setBots(prev => prev.map(b =>
                                botsWithoutAutoLogin.some(ab => ab.sip_id === b.sip_id) ? { ...b, auto_login: 1 } : b
                            ))
                        }
                        if (checkboxStates[1] && checkboxStates[0]) {
                            for (const bot of botsWithoutAutoLogin) {
                                try {
                                    await clientApi(`/bots/${bot.sip_id}/auto-revive`, {
                                        method: 'POST',
                                        body: JSON.stringify({ enabled: true, dialer_id: bot.dialer_id })
                                    })
                                } catch (err) { console.error(`Failed to enable auto-revive for ${bot.sip_id}:`, err) }
                            }
                            setBots(prev => prev.map(b =>
                                botsWithoutAutoLogin.some(ab => ab.sip_id === b.sip_id) ? { ...b, auto_revive: 1 } : b
                            ))
                        }

                        executeBulkCommand(command, sipIds)
                    }
                })
                return
            }
        }

        // No confirmation needed, execute directly
        executeBulkCommand(command, sipIds)
    }

    // Bulk toggle auto-login
    const bulkToggleAutoLogin = async (enabled) => {
        const sipIds = [...selectedBots]
        if (sipIds.length === 0) return

        setBulkLoading(true)
        setBots(prev => prev.map(b => sipIds.includes(b.sip_id) ? { ...b, auto_login: enabled ? 1 : 0 } : b))

        for (const sipId of sipIds) {
            try {
                await clientApi(`/bots/${sipId}/auto-login`, {
                    method: 'POST',
                    body: JSON.stringify({ enabled, dialer_id: resolveDialerId(sipId) })
                })
            } catch (err) {
                console.error(`Failed to update auto-login for ${sipId}:`, err)
            }
        }

        showToast(`Auto-login ${enabled ? 'enabled' : 'disabled'} for ${sipIds.length} bots`, 'success')
        setBulkLoading(false)
    }

    // Bulk toggle auto-revive
    const bulkToggleAutoRevive = async (enabled) => {
        const sipIds = [...selectedBots]
        const eligibleBots = enabled
            ? bots.filter(b => sipIds.includes(b.sip_id) && b.auto_login)
            : bots.filter(b => sipIds.includes(b.sip_id))

        if (eligibleBots.length === 0) {
            showToast('Enable Auto-Login first for selected bots', 'error')
            return
        }

        setBulkLoading(true)
        setBots(prev => prev.map(b =>
            eligibleBots.some(eb => eb.sip_id === b.sip_id) ? { ...b, auto_revive: enabled ? 1 : 0 } : b
        ))

        for (const bot of eligibleBots) {
            try {
                await clientApi(`/bots/${bot.sip_id}/auto-revive`, {
                    method: 'POST',
                    body: JSON.stringify({ enabled, dialer_id: bot.dialer_id })
                })
            } catch (err) {
                console.error(`Failed to update auto-revive for ${bot.sip_id}:`, err)
            }
        }

        showToast(`Auto-revive ${enabled ? 'enabled' : 'disabled'} for ${eligibleBots.length} bots`, 'success')
        setBulkLoading(false)
    }

    const bulkSwitchMode = async (mode) => {
        const sipIds = [...selectedBots]
        const eligibleBots = bots.filter(b => sipIds.includes(b.sip_id))
        if (eligibleBots.length === 0) return

        if (!window.confirm(`Switch ${eligibleBots.length} bot(s) to ${mode.toUpperCase()} mode?\n\nBots will use the new avatar on next call.`)) return

        setBulkLoading(true)
        let successCount = 0
        let failMsg = ''

        for (const bot of eligibleBots) {
            try {
                const data = await clientApi(`/bots/${bot.sip_id}/mode`, {
                    method: 'POST',
                    body: JSON.stringify({ mode, dialer_id: bot.dialer_id })
                })
                if (data.success) {
                    successCount++
                    setBots(prev => prev.map(b => b.sip_id === bot.sip_id ? { ...b, avatar_id: data.avatar_id, avatar_name: data.avatar_name } : b))
                } else {
                    if (!failMsg) failMsg = data.error
                }
            } catch (err) {
                if (!failMsg) failMsg = err.message
            }
        }

        if (successCount > 0) showToast(`${successCount} bot(s) switched to ${mode} mode`, 'success')
        if (failMsg) showToast(failMsg, 'error')
        setBulkLoading(false)
    }

    // Execute bulk VM action directly (helper)
    const executeBulkVMAction = async (action, botsWithVM) => {
        setBulkLoading(true)

        for (const bot of botsWithVM) {
            try {
                await clientApi('/vm/action', {
                    method: 'POST',
                    body: JSON.stringify({ sip_id: bot.sip_id, action, dialer_id: bot.dialer_id })
                })
            } catch (err) {
                console.error(`Failed to ${action} VM for ${bot.sip_id}:`, err)
            }
        }

        showToast(`VM ${action} command sent to ${botsWithVM.length} VMs`, 'success')
        setBulkLoading(false)
        setSelectedBots(new Set())
    }

    // Bulk VM actions with auto-toggle confirmation
    const bulkVMAction = async (action) => {
        const sipIds = [...selectedBots]
        const botsWithVM = bots.filter(b => sipIds.includes(b.sip_id) && b.vmid && b.server_ip)

        if (botsWithVM.length === 0) {
            showToast('No VMs linked to selected bots', 'error')
            return
        }

        // For STOP or RESTART: offer to disable auto features
        if (action === 'stop' || action === 'restart') {
            const botsWithAutoLogin = botsWithVM.filter(b => b.auto_login === 1 || b.auto_login === true)
            const botsWithAutoRevive = botsWithVM.filter(b => b.auto_revive === 1 || b.auto_revive === true)

            if (botsWithAutoLogin.length > 0 || botsWithAutoRevive.length > 0) {
                const checkboxes = []
                if (botsWithAutoLogin.length > 0) {
                    checkboxes.push({ label: `Disable Auto-Login (${botsWithAutoLogin.length} bots)`, checked: false })
                }
                if (botsWithAutoRevive.length > 0) {
                    checkboxes.push({ label: `Disable Auto-Revive (${botsWithAutoRevive.length} bots)`, checked: false })
                }

                setConfirmAction({
                    title: `${action.charAt(0).toUpperCase() + action.slice(1)} ${botsWithVM.length} VMs?`,
                    message: `Some bots have auto features enabled. Also disable them?`,
                    checkboxes,
                    confirmText: `${action.charAt(0).toUpperCase() + action.slice(1)} VMs`,
                    onConfirm: async (checkboxStates) => {
                        setConfirmAction(null)

                        // Disable auto features if checked
                        let checkboxIdx = 0
                        if (botsWithAutoLogin.length > 0 && checkboxStates[checkboxIdx]) {
                            for (const bot of botsWithAutoLogin) {
                                try {
                                    await clientApi(`/bots/${bot.sip_id}/auto-login`, {
                                        method: 'POST',
                                        body: JSON.stringify({ enabled: false, dialer_id: bot.dialer_id })
                                    })
                                } catch (err) { console.error(`Failed to disable auto-login for ${bot.sip_id}:`, err) }
                            }
                            setBots(prev => prev.map(b =>
                                botsWithAutoLogin.some(ab => ab.sip_id === b.sip_id) ? { ...b, auto_login: 0 } : b
                            ))
                            checkboxIdx++
                        }
                        if (botsWithAutoRevive.length > 0 && checkboxStates[checkboxIdx]) {
                            for (const bot of botsWithAutoRevive) {
                                try {
                                    await clientApi(`/bots/${bot.sip_id}/auto-revive`, {
                                        method: 'POST',
                                        body: JSON.stringify({ enabled: false, dialer_id: bot.dialer_id })
                                    })
                                } catch (err) { console.error(`Failed to disable auto-revive for ${bot.sip_id}:`, err) }
                            }
                            setBots(prev => prev.map(b =>
                                botsWithAutoRevive.some(ab => ab.sip_id === b.sip_id) ? { ...b, auto_revive: 0 } : b
                            ))
                        }

                        executeBulkVMAction(action, botsWithVM)
                    }
                })
                return
            }
        }

        // For START: offer to enable auto features
        if (action === 'start') {
            const botsWithoutAutoLogin = botsWithVM.filter(b => !b.auto_login || b.auto_login === 0)

            if (botsWithoutAutoLogin.length > 0) {
                setConfirmAction({
                    title: `Start ${botsWithVM.length} VMs?`,
                    message: `${botsWithoutAutoLogin.length} bot(s) don't have Auto-Login enabled. Would you like to enable it?`,
                    checkboxes: [
                        { label: `Enable Auto-Login (${botsWithoutAutoLogin.length} bots)`, checked: false },
                        { label: `Enable Auto-Revive (${botsWithoutAutoLogin.length} bots)`, checked: false }
                    ],
                    confirmText: 'Start VMs',
                    onConfirm: async (checkboxStates) => {
                        setConfirmAction(null)

                        // Enable auto features if checked
                        if (checkboxStates[0]) {
                            for (const bot of botsWithoutAutoLogin) {
                                try {
                                    await clientApi(`/bots/${bot.sip_id}/auto-login`, {
                                        method: 'POST',
                                        body: JSON.stringify({ enabled: true, dialer_id: bot.dialer_id })
                                    })
                                } catch (err) { console.error(`Failed to enable auto-login for ${bot.sip_id}:`, err) }
                            }
                            setBots(prev => prev.map(b =>
                                botsWithoutAutoLogin.some(ab => ab.sip_id === b.sip_id) ? { ...b, auto_login: 1 } : b
                            ))
                        }
                        if (checkboxStates[1] && checkboxStates[0]) {
                            for (const bot of botsWithoutAutoLogin) {
                                try {
                                    await clientApi(`/bots/${bot.sip_id}/auto-revive`, {
                                        method: 'POST',
                                        body: JSON.stringify({ enabled: true, dialer_id: bot.dialer_id })
                                    })
                                } catch (err) { console.error(`Failed to enable auto-revive for ${bot.sip_id}:`, err) }
                            }
                            setBots(prev => prev.map(b =>
                                botsWithoutAutoLogin.some(ab => ab.sip_id === b.sip_id) ? { ...b, auto_revive: 1 } : b
                            ))
                        }

                        executeBulkVMAction(action, botsWithVM)
                    }
                })
                return
            }
        }

        // Default confirmation without checkboxes
        setConfirmAction({
            title: `${action.charAt(0).toUpperCase() + action.slice(1)} ${botsWithVM.length} VMs?`,
            message: `Are you sure you want to ${action} ${botsWithVM.length} VMs?`,
            confirmText: `${action.charAt(0).toUpperCase() + action.slice(1)} VMs`,
            onConfirm: async () => {
                setConfirmAction(null)
                executeBulkVMAction(action, botsWithVM)
            }
        })
    }

    // Fetch voice sets for dropdown
    const fetchVoiceSets = useCallback(async () => {
        try {
            const data = await clientApi('/voice-sets')
            if (data.success) setVoiceSets(data.voice_sets)
        } catch (err) {
            console.error('Failed to fetch voice sets:', err)
        }
    }, [clientApi])

    // Open edit modal
    const openEditModal = async (bot) => {
        setEditBot(bot)
        setShowEditModal(true)
        setEditLoading(true)

        // Fetch voice sets if not loaded
        if (voiceSets.length === 0) {
            await fetchVoiceSets()
        }

        // Fetch current license data
        try {
            const data = await clientApi(`/bots/${bot.sip_id}/license`)
            if (data.success) {
                setLicenseData({
                    voice_set_pool: data.license.voice_set_pool || []
                })
            }
        } catch (err) {
            console.error('Failed to fetch license:', err)
            showToast('Failed to load license settings', 'error')
        }
        setEditLoading(false)
    }

    // Close edit modal
    const closeEditModal = () => {
        setShowEditModal(false)
        setEditBot(null)
        setEditBots([])
        setBulkEditMode(false)
        setLicenseData({
            voice_set_pool: []
        })
        // Stop any playing audio
        if (audioRef.current) {
            audioRef.current.pause()
            audioRef.current.src = ''
        }
        setPlayingVoiceSet(null)
    }

    // Open bulk edit modal
    const openBulkEditModal = async () => {
        const sipIds = [...selectedBots]
        const botsToEdit = bots.filter(b => sipIds.includes(b.sip_id))

        if (botsToEdit.length === 0) {
            showToast('No bots selected', 'error')
            return
        }

        setEditBots(botsToEdit)
        setBulkEditMode(true)
        setShowEditModal(true)
        setEditLoading(true)

        // Fetch voice sets if not loaded
        if (voiceSets.length === 0) {
            await fetchVoiceSets()
        }

        // For bulk edit, fetch voice sets from all selected bots and find intersection
        try {
            const allVoiceSets = []
            for (const bot of botsToEdit) {
                const data = await clientApi(`/bots/${bot.sip_id}/license`)
                if (data.success && data.license.voice_set_pool) {
                    allVoiceSets.push(data.license.voice_set_pool)
                }
            }

            // Find intersection (voice sets common to all bots)
            let commonVoiceSets = []
            if (allVoiceSets.length > 0) {
                commonVoiceSets = allVoiceSets.reduce((acc, curr) =>
                    acc.filter(id => curr.includes(id))
                )
            }

            setLicenseData({
                voice_set_pool: commonVoiceSets
            })
        } catch (err) {
            console.error('Failed to fetch voice sets for bulk edit:', err)
            setLicenseData({ voice_set_pool: [] })
        }
        setEditLoading(false)
    }

    // Save license changes
    const saveLicenseChanges = async () => {
        setEditSaving(true)

        if (bulkEditMode) {
            // Bulk edit - apply to all selected bots
            const botsToUpdate = editBots
            let successCount = 0
            let failCount = 0

            // Only include voice_set_pool
            const updateData = {}
            if (licenseData.voice_set_pool && licenseData.voice_set_pool.length > 0) {
                updateData.voice_set_pool = licenseData.voice_set_pool
            }

            if (Object.keys(updateData).length === 0) {
                showToast('Please select at least one voice set', 'error')
                setEditSaving(false)
                return
            }

            for (const bot of botsToUpdate) {
                try {
                    const data = await clientApi(`/bots/${bot.sip_id}/license`, {
                        method: 'PUT',
                        body: JSON.stringify({ ...updateData, dialer_id: bot.dialer_id })
                    })
                    if (data.success) successCount++
                    else failCount++
                } catch (err) {
                    console.error(`Failed to update ${bot.sip_id}:`, err)
                    failCount++
                }
            }

            if (successCount > 0) {
                showToast(`Voice sets updated for ${successCount} bot${successCount > 1 ? 's' : ''}${failCount > 0 ? `, ${failCount} failed` : ''}`, 'success')
                // Show restart notification
                setTimeout(() => {
                    showToast('Restart VMs to apply voice set changes', 'info')
                }, 500)
            } else {
                showToast('Failed to update bots', 'error')
            }

            closeEditModal()
            setSelectedBots(new Set())
        } else {
            // Single bot edit
            if (!editBot) return

            try {
                const data = await clientApi(`/bots/${editBot.sip_id}/license`, {
                    method: 'PUT',
                    body: JSON.stringify({ voice_set_pool: licenseData.voice_set_pool, dialer_id: editBot.dialer_id })
                })

                if (data.success) {
                    showToast('Voice sets updated successfully', 'success')
                    // Show restart notification
                    setTimeout(() => {
                        showToast('Restart VM to apply voice set changes', 'info')
                    }, 500)
                    closeEditModal()
                } else {
                    showToast(data.error || 'Failed to update voice sets', 'error')
                }
            } catch (err) {
                console.error('Failed to save voice sets:', err)
                showToast('Failed to save voice sets', 'error')
            }
        }
        setEditSaving(false)
    }

    // Toggle voice set in pool
    const toggleVoiceSet = (vsId) => {
        setLicenseData(prev => {
            const pool = prev.voice_set_pool || []
            if (pool.includes(vsId)) {
                return { ...prev, voice_set_pool: pool.filter(id => id !== vsId) }
            } else {
                return { ...prev, voice_set_pool: [...pool, vsId] }
            }
        })
    }

    // Preview voice set audio
    const previewVoiceSet = async (vsId, e) => {
        e.stopPropagation() // Don't toggle selection

        // If already playing this voice set, stop it
        if (playingVoiceSet === vsId) {
            audioRef.current.pause()
            audioRef.current.src = ''
            setPlayingVoiceSet(null)
            return
        }

        setAudioLoading(vsId)
        setPlayingVoiceSet(vsId)

        try {
            // Get avatar_id from current bot(s) for context-relevant audio
            const avatarId = editBot?.avatar_id || editBots[0]?.avatar_id
            // Fetch sample audio from voice set with avatar context
            const data = await clientApi(`/voice-sets/${vsId}/sample${avatarId ? `?avatar_id=${avatarId}` : ''}`)
            if (data.success && data.audio_url) {
                const audio = audioRef.current
                // Reset event handlers first to prevent stale callbacks
                audio.onended = null
                audio.onerror = null
                // Set source and load
                audio.src = data.audio_url
                audio.load()
                // Set up event handlers
                audio.onended = () => setPlayingVoiceSet(null)
                // Play and handle errors via promise rejection
                await audio.play()
            } else {
                showToast('No sample audio available', 'info')
                setPlayingVoiceSet(null)
            }
        } catch (err) {
            console.error('Failed to preview voice set:', err)
            showToast('Failed to play audio', 'error')
            setPlayingVoiceSet(null)
        }
        setAudioLoading(null)
    }

    return (
        <div className="bt-container">
            {/* Toast Container */}
            <div className="bt-toast-container">
                {toasts.map(t => (
                    <Toast key={t.id} message={t.message} type={t.type} onClose={() => removeToast(t.id)} />
                ))}
            </div>

            {/* Confirmation Dialog */}
            {confirmAction && (
                <ConfirmDialog
                    title={confirmAction.title}
                    message={confirmAction.message}
                    checkboxes={confirmAction.checkboxes || []}
                    confirmText={confirmAction.confirmText}
                    onConfirm={confirmAction.onConfirm}
                    onCancel={() => setConfirmAction(null)}
                />
            )}

            {/* Header */}
            <div className="bt-header">
                <div className="bt-header-left">
                    <h2><Bot size={18} /> My Bots</h2>
                    <span className="bt-count">{bots.length}</span>
                </div>

                {/* Date Selector */}
                <div className="bt-date-selector">
                    {DATE_PRESETS.map(p => (
                        <button
                            key={p.key}
                            className={`bt-date-btn ${datePreset === p.key ? 'active' : ''}`}
                            onClick={() => {
                                setDatePreset(p.key)
                                if (p.key === 'custom') setShowDatePicker(true)
                                else setShowDatePicker(false)
                            }}
                        >
                            {p.label}
                        </button>
                    ))}
                    {showDatePicker && datePreset === 'custom' && (
                        <div className="bt-date-picker">
                            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
                            <span>to</span>
                            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
                            <button className="bt-date-apply" onClick={() => { setShowDatePicker(false); fetchBots(); }}>Apply</button>
                        </div>
                    )}
                </div>

                {/* Dialer filter - only shown when more than one dialer is linked.
                    Lets the client isolate one dialer when two share SIP numbers. */}
                {dialerOptions.length > 1 && (
                    <div className="bt-dialer-filter">
                        <select
                            value={selectedDialer}
                            onChange={(e) => { setSelectedDialer(e.target.value); setSelectedBots(new Set()) }}
                            title="Filter bots by dialer"
                            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border-color, #d1d5db)', background: 'var(--bg-card, #fff)', color: 'inherit', fontSize: '13px', cursor: 'pointer', maxWidth: '220px' }}
                        >
                            <option value="">All Dialers ({dialerOptions.length})</option>
                            {dialerOptions.map(d => (
                                <option key={d.id} value={d.id}>{d.name}</option>
                            ))}
                        </select>
                    </div>
                )}

                <button className="bt-refresh-btn" onClick={fetchBots} disabled={loading}>
                    <RefreshCw size={14} className={loading ? 'spinning' : ''} />
                </button>
            </div>

            {/* Stats Row */}
            <div className="bt-stats-row">
                <div className="bt-status-cards">
                    {[
                        { key: 'active', count: statusStats.active, icon: Activity, color: '#10b981', label: 'active' },
                        { key: 'dial_level_0', count: statusStats.dial_level_0, icon: AlertTriangle, color: '#ef4444', label: 'dial lvl 0' },
                        { key: 'waiting', count: statusStats.waiting, icon: Clock, color: '#f59e0b', label: 'logged out' },
                        { key: 'offline', count: statusStats.offline, icon: WifiOff, color: '#6b7280', label: 'offline' }
                    ].map(s => (
                        <button
                            key={s.key}
                            className={`bt-stat-card bt-stat-${s.key} ${selectedStatus === s.key ? 'active' : ''}`}
                            onClick={() => setSelectedStatus(selectedStatus === s.key ? '' : s.key)}
                        >
                            <s.icon size={14} />
                            <span className="bt-stat-num">{s.count}</span>
                            <span className="bt-stat-label">{s.label}</span>
                        </button>
                    ))}
                </div>

                <div className="bt-dispo-cards">
                    <div className="bt-dispo-card" title="Total Calls"><Phone size={12} /><span>{totals.calls}</span></div>
                    <div className="bt-dispo-card bt-dispo-xfer" title="Transfers"><PhoneForwarded size={12} /><span>{totals.xfer}</span></div>
                    <div className="bt-dispo-card bt-dispo-am" title="AM"><Voicemail size={12} /><span>{totals.am}</span></div>
                    <div className="bt-dispo-card bt-dispo-ni" title="NI"><PhoneOff size={12} /><span>{totals.ni}</span></div>
                    <div className="bt-dispo-card bt-dispo-na" title="NA"><PhoneMissed size={12} /><span>{totals.na}</span></div>
                    <div className="bt-dispo-card bt-dispo-dnc" title="DNC"><Ban size={12} /><span>{totals.dnc}</span></div>
                    <div className="bt-dispo-card bt-dispo-dnq" title="DNQ"><UserX size={12} /><span>{totals.dnq}</span></div>
                </div>

                {/* Bulk Actions */}
                {selectedBots.size === 0 && (
                    <div className="bt-bulk-actions" style={{ justifyContent: 'flex-end' }}>
                        <button className="bt-bulk-btn" onClick={() => setShowRangeModal(true)} style={{ borderColor: 'rgba(100,116,139,0.3)', color: '#64748b' }}>
                            <BarChart3 size={12} /> Select Range
                        </button>
                    </div>
                )}
                {selectedBots.size > 0 && (
                    <div className="bt-bulk-actions">
                        <span className="bt-bulk-count">{selectedBots.size} selected</span>
                        <button className="bt-bulk-btn bt-bulk-start" onClick={() => bulkCommand('start')} disabled={bulkLoading} title="Login bots to ViciDial">
                            <Play size={12} /> Login
                        </button>
                        <button className="bt-bulk-btn bt-bulk-stop" onClick={() => bulkCommand('stop')} disabled={bulkLoading} title="Logout bots from ViciDial">
                            <Square size={12} /> Logout
                        </button>
                        <span className="bt-bulk-divider" />
                        <button className="bt-bulk-btn bt-bulk-auto-on" onClick={() => bulkToggleAutoLogin(true)} disabled={bulkLoading} title="Enable Auto-Login">
                            <ToggleRight size={14} /> Login On
                        </button>
                        <button className="bt-bulk-btn bt-bulk-auto-off" onClick={() => bulkToggleAutoLogin(false)} disabled={bulkLoading} title="Disable Auto-Login">
                            <ToggleLeft size={14} /> Login Off
                        </button>
                        <button className="bt-bulk-btn bt-bulk-revive-on" onClick={() => bulkToggleAutoRevive(true)} disabled={bulkLoading} title="Enable Auto-Revive">
                            <RefreshCcw size={14} /> Revive On
                        </button>
                        <button className="bt-bulk-btn bt-bulk-revive-off" onClick={() => bulkToggleAutoRevive(false)} disabled={bulkLoading} title="Disable Auto-Revive">
                            <RefreshCcw size={14} /> Revive Off
                        </button>
                        <span className="bt-bulk-divider" />
                        <button className="bt-bulk-btn bt-bulk-vm-start" onClick={() => bulkVMAction('start')} disabled={bulkLoading} title="Start all selected VMs">
                            <Power size={12} /> Start VM
                        </button>
                        <button className="bt-bulk-btn bt-bulk-vm-restart" onClick={() => bulkVMAction('restart')} disabled={bulkLoading} title="Restart all selected VMs">
                            <RotateCcw size={12} /> Restart VM
                        </button>
                        <button className="bt-bulk-btn bt-bulk-vm-stop" onClick={() => bulkVMAction('stop')} disabled={bulkLoading} title="Stop all selected VMs">
                            <PowerOff size={12} /> Stop VM
                        </button>
                        <span className="bt-bulk-divider" />
                        <button className="bt-bulk-btn bt-bulk-edit" onClick={openBulkEditModal} disabled={bulkLoading} title="Modify voice sets for selected bots">
                            <Mic size={12} /> Voice Sets
                        </button>
                        {clientAvatars.length > 0 && (
                            <button className="bt-bulk-btn" onClick={() => setShowAvatarModal(true)} disabled={bulkLoading}
                                style={{ borderColor: 'rgba(139, 92, 246, 0.3)', color: '#8b5cf6' }}
                                title="Switch avatar for selected bots">
                                <Users size={12} /> Avatar
                            </button>
                        )}
                        <button className="bt-bulk-btn" onClick={() => setShowRangeModal(true)} style={{ borderColor: 'rgba(100,116,139,0.3)', color: '#64748b' }}>
                            <BarChart3 size={12} /> Range
                        </button>
                        <button className="bt-bulk-btn bt-bulk-clear" onClick={() => setSelectedBots(new Set())}>Clear</button>
                    </div>
                )}
            </div>

            {/* Table */}
            <div className="bt-table-wrapper">
                {loading && bots.length === 0 ? (
                    <div className="bt-loading"><div className="bt-spinner" /><span>Loading...</span></div>
                ) : filteredBots.length === 0 ? (
                    <div className="bt-empty"><Bot size={32} /><p>No bots found</p></div>
                ) : (
                    <table className="bt-table">
                        <thead>
                            <tr>
                                <th className="bt-th-check">
                                    <button className="bt-check-btn" onClick={toggleSelectAll}>
                                        {selectedBots.size === filteredBots.length && filteredBots.length > 0
                                            ? <CheckSquare size={14} />
                                            : <SquareIcon size={14} />}
                                    </button>
                                </th>
                                <th className="bt-th-icon" title="Auto Login"><ToggleRight size={13} /></th>
                                <th className="bt-th-icon" title="Auto Revive"><RefreshCcw size={13} /></th>
                                <th>SIP</th>
                                <th>Status</th>
                                <th className="bt-th-icon" title="Total Calls"><Phone size={11} /></th>
                                <th className="bt-th-icon bt-col-xfer" title="Transfers"><PhoneForwarded size={11} /></th>
                                <th className="bt-th-icon bt-col-am" title="AM"><Voicemail size={11} /></th>
                                <th className="bt-th-icon bt-col-ni" title="NI"><PhoneOff size={11} /></th>
                                <th className="bt-th-icon bt-col-na" title="NA"><PhoneMissed size={11} /></th>
                                <th className="bt-th-icon bt-col-dnc" title="DNC"><Ban size={11} /></th>
                                <th className="bt-th-icon bt-col-dnq" title="DNQ"><UserX size={11} /></th>
                                <th className="bt-th-voice" title="Voice Sets">
                                    <Mic size={11} />
                                </th>
                                <th>HB</th>
                                <th className="bt-th-actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredBots.map(bot => {
                                const status = bot.effective_status || 'offline'
                                const cfg = getStatusConfig(status)
                                const Icon = cfg.icon
                                const cmd = commandState[bot.sip_id]
                                const isProc = !!cmd
                                const isSelected = selectedBots.has(bot.sip_id)
                                const isExpanded = expandedBot === bot.sip_id
                                const currentVmStatus = vmStatus[bot.sip_id]

                                return (
                                    <React.Fragment key={`${bot.sip_id}-${bot.dialer_id}`}>
                                        <tr className={`${status === 'active' ? 'row-active' : ''} ${isSelected ? 'row-selected' : ''} ${isExpanded ? 'row-expanded' : ''}`}>
                                            <td className="bt-td-check">
                                                <button className="bt-check-btn" onClick={() => toggleSelect(bot.sip_id)}>
                                                    {isSelected ? <CheckSquare size={14} /> : <SquareIcon size={14} />}
                                                </button>
                                            </td>
                                            <td className="bt-td-auto">
                                                <button
                                                    className={`bt-auto-btn ${bot.auto_login ? 'bt-auto-on' : ''}`}
                                                    onClick={() => toggleAutoLogin(bot)}
                                                    title={bot.auto_login ? 'Auto-login enabled - click to disable' : 'Auto-login disabled - click to enable'}
                                                >
                                                    {bot.auto_login ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                                                </button>
                                            </td>
                                            <td className="bt-td-auto">
                                                <button
                                                    className={`bt-revive-btn ${bot.auto_revive ? 'bt-revive-on' : ''} ${!bot.auto_login ? 'bt-revive-disabled' : ''}`}
                                                    onClick={() => toggleAutoRevive(bot)}
                                                    title={!bot.auto_login ? 'Enable Auto-Login first' : bot.auto_revive ? `Auto-revive enabled (${bot.auto_revive_count || 0}/3 revives)` : 'Auto-revive disabled'}
                                                >
                                                    <RefreshCcw size={14} />
                                                </button>
                                            </td>
                                            <td className="bt-td-sip">
                                                <span>{bot.sip_id}</span>
                                            </td>
                                            <td>
                                                {isProc ? (
                                                    <div className={`bt-progress-status ${cmd.error ? 'bt-progress-error' : ''}`}>
                                                        {cmd.error ? (
                                                            <X size={12} className="bt-error-icon" />
                                                        ) : (
                                                            <span className="bt-progress-spinner" />
                                                        )}
                                                        <span className="bt-progress-text">{cmd.status}</span>
                                                        {cmd.error && (
                                                            <button
                                                                className="bt-dismiss-error"
                                                                onClick={() => setCommandState(prev => { const n = { ...prev }; delete n[bot.sip_id]; return n })}
                                                                title="Dismiss"
                                                            >
                                                                <X size={10} />
                                                            </button>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="bt-status-badge" style={{ '--c': cfg.color }}>
                                                        <Icon size={10} />{cfg.label}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="bt-td-num">{bot.calls || 0}</td>
                                            <td className="bt-td-num bt-col-xfer">{bot.xfer || 0}</td>
                                            <td className="bt-td-num bt-col-am">{bot.am || 0}</td>
                                            <td className="bt-td-num bt-col-ni">{bot.ni || 0}</td>
                                            <td className="bt-td-num bt-col-na">{bot.na || 0}</td>
                                            <td className="bt-td-num bt-col-dnc">{bot.dnc || 0}</td>
                                            <td className="bt-td-num bt-col-dnq">{bot.dnq || 0}</td>
                                            <td className="bt-td-voice">
                                                <span className="bt-voice-gender bt-voice-male" title="Male Voice Sets">
                                                    <User size={10} /> {bot.voice_sets_male || 0}
                                                </span>
                                                <span className="bt-voice-gender bt-voice-female" title="Female Voice Sets">
                                                    <User size={10} /> {bot.voice_sets_female || 0}
                                                </span>
                                            </td>
                                            <td className="bt-td-hb">{formatTimeAgo(bot.last_heartbeat)}</td>
                                            <td className="bt-td-actions">
                                                <div className="bt-action-group">
                                                    {(status === 'waiting' || status === 'offline') && (
                                                        <button className="bt-action-btn bt-action-start" title="Start Bot"
                                                            onClick={() => sendCommand(bot.sip_id, 'start')} disabled={isProc}>
                                                            <Play size={11} />
                                                        </button>
                                                    )}
                                                    {(status === 'active' || status === 'running' || status === 'dial_level_0') && (
                                                        <button className="bt-action-btn bt-action-stop" title="Stop Bot"
                                                            onClick={() => sendCommand(bot.sip_id, 'stop')} disabled={isProc}>
                                                            <Square size={11} />
                                                        </button>
                                                    )}
                                                    {/* Voice Sets Button */}
                                                    <button
                                                        className="bt-action-btn bt-action-edit"
                                                        title="Modify Voice Sets"
                                                        onClick={() => openEditModal(bot)}
                                                        disabled={isProc}
                                                    >
                                                        <Mic size={11} />
                                                    </button>
                                                    {/* VM Expand Button */}
                                                    <button
                                                        className={`bt-action-btn bt-action-vm ${!bot.server_ip ? 'bt-action-disabled' : ''} ${bot.vmid ? 'bt-action-linked' : ''} ${isExpanded ? 'bt-action-expanded' : ''}`}
                                                        title={!bot.server_ip ? 'No server' : bot.vmid ? `VM ${bot.vmid}` : 'No VM'}
                                                        onClick={() => toggleExpand(bot)}
                                                        disabled={isProc || !bot.server_ip || !bot.vmid}
                                                    >
                                                        <Server size={11} />
                                                        {isExpanded ? <ChevronUp size={9} /> : <ChevronDown size={9} />}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                        {/* Expanded VM Controls Row */}
                                        {isExpanded && (
                                            <tr className="bt-vm-row">
                                                <td colSpan={14}>
                                                    <div className="bt-vm-panel">
                                                        <div className="bt-vm-panel-header">
                                                            <Server size={14} />
                                                            <span>VM Controls</span>
                                                            {bot.vmid && (
                                                                <span className="bt-vm-id">VM {bot.vmid}</span>
                                                            )}
                                                            {currentVmStatus?.loading && (
                                                                <span className="bt-vm-loading">Checking status...</span>
                                                            )}
                                                            {currentVmStatus?.status && !currentVmStatus.loading && (
                                                                <span className={`bt-vm-status bt-vm-status-${currentVmStatus.status}`}>
                                                                    {currentVmStatus.status === 'running' ? <Power size={12} /> : <PowerOff size={12} />}
                                                                    {currentVmStatus.status}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="bt-vm-panel-body">
                                                            <div className="bt-vm-actions">
                                                                {currentVmStatus?.status === 'stopped' && (
                                                                    <button className="bt-vm-action-btn bt-vm-start" onClick={() => vmAction('start', bot)}>
                                                                        <Play size={14} /> Start VM
                                                                    </button>
                                                                )}
                                                                {currentVmStatus?.status === 'running' && (
                                                                    <>
                                                                        <button className="bt-vm-action-btn bt-vm-stop" onClick={() => vmAction('stop', bot)}>
                                                                            <Square size={14} /> Stop VM
                                                                        </button>
                                                                        <button className="bt-vm-action-btn bt-vm-restart" onClick={() => vmAction('restart', bot)}>
                                                                            <RotateCcw size={14} /> Reset VM
                                                                        </button>
                                                                    </>
                                                                )}
                                                                {(!currentVmStatus || currentVmStatus.status === 'unknown') && !currentVmStatus?.loading && (
                                                                    <>
                                                                        <button className="bt-vm-action-btn bt-vm-start" onClick={() => vmAction('start', bot)}>
                                                                            <Play size={14} /> Start VM
                                                                        </button>
                                                                        <button className="bt-vm-action-btn bt-vm-stop" onClick={() => vmAction('stop', bot)}>
                                                                            <Square size={14} /> Stop VM
                                                                        </button>
                                                                        <button className="bt-vm-action-btn bt-vm-restart" onClick={() => vmAction('restart', bot)}>
                                                                            <RotateCcw size={14} /> Reset VM
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                )
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Voice Sets Modal */}
            {showEditModal && (editBot || bulkEditMode) && (
                <div className="bt-edit-modal-overlay" onClick={closeEditModal}>
                    <div className="bt-edit-modal" onClick={e => e.stopPropagation()}>
                        <div className="bt-edit-modal-header">
                            <h3>
                                <Mic size={18} />
                                {bulkEditMode
                                    ? `Voice Sets: ${editBots.length} Bot${editBots.length > 1 ? 's' : ''}`
                                    : `Voice Sets: ${editBot?.sip_id}`
                                }
                            </h3>
                            <button className="bt-edit-modal-close" onClick={closeEditModal}>
                                <X size={18} />
                            </button>
                        </div>

                        {editLoading ? (
                            <div className="bt-edit-modal-loading">
                                <Loader2 size={24} className="spinning" />
                                <span>Loading voice sets...</span>
                            </div>
                        ) : (
                            <div className="bt-edit-modal-body">
                                {/* Voice Set Pool */}
                                <div className="bt-edit-section">
                                    <h4><Mic size={14} /> Voice Sets</h4>
                                    <p className="bt-edit-hint">Select voice sets to rotate through on each call. Click the play button to preview.</p>
                                    <div className="bt-voice-set-grid">
                                        {voiceSets.map(vs => (
                                            <div
                                                key={vs.id}
                                                className={`bt-voice-set-item ${licenseData.voice_set_pool?.includes(vs.id) ? 'selected' : ''}`}
                                                onClick={() => toggleVoiceSet(vs.id)}
                                            >
                                                <button
                                                    className="bt-vs-preview"
                                                    onClick={(e) => previewVoiceSet(vs.id, e)}
                                                    title="Preview voice"
                                                >
                                                    {audioLoading === vs.id ? (
                                                        <Loader2 size={12} className="spinning" />
                                                    ) : playingVoiceSet === vs.id ? (
                                                        <Pause size={12} />
                                                    ) : (
                                                        <Play size={12} />
                                                    )}
                                                </button>
                                                <span className="bt-vs-name">{vs.name}</span>
                                                {vs.gender && <span className="bt-vs-gender">{vs.gender}</span>}
                                                {licenseData.voice_set_pool?.includes(vs.id) && <Check size={12} className="bt-vs-check" />}
                                            </div>
                                        ))}
                                    </div>
                                    {licenseData.voice_set_pool?.length > 0 && (
                                        <div className="bt-selected-count">
                                            {licenseData.voice_set_pool.length} voice set{licenseData.voice_set_pool.length > 1 ? 's' : ''} selected
                                        </div>
                                    )}
                                </div>

                                {/* Restart notice */}
                                <div className="bt-edit-notice">
                                    <AlertCircle size={14} />
                                    <span>Changes will take effect after restarting the VM</span>
                                </div>
                            </div>
                        )}

                        <div className="bt-edit-modal-footer">
                            <button className="bt-edit-cancel" onClick={closeEditModal}>Cancel</button>
                            <button
                                className="bt-edit-save"
                                onClick={saveLicenseChanges}
                                disabled={editSaving || editLoading}
                            >
                                {editSaving ? <><Loader2 size={14} className="spinning" /> Saving...</> : <><Check size={14} /> Save Changes</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Select Range Modal */}
            {showRangeModal && (
                <div className="bt-edit-modal-overlay" onClick={() => setShowRangeModal(false)}>
                    <div className="bt-edit-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
                        <div className="bt-edit-modal-header">
                            <h3 style={{ margin: 0, fontSize: '1rem' }}>
                                <BarChart3 size={18} style={{ marginRight: 8 }} />
                                Select SIP Range
                            </h3>
                            <button className="bt-edit-modal-close" onClick={() => setShowRangeModal(false)}><X size={18} /></button>
                        </div>
                        <div style={{ padding: '16px 20px' }}>
                            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 16 }}>
                                Select all bots with SIP IDs within the specified range.
                            </p>
                            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>From SIP</label>
                                    <input type="text" value={rangeFrom} onChange={e => setRangeFrom(e.target.value)} placeholder="e.g. 8026"
                                        style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border-color)', borderRadius: 6, background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                                        onKeyDown={e => e.key === 'Enter' && selectRange()} />
                                </div>
                                <span style={{ color: 'var(--text-muted)', paddingTop: 18 }}>to</span>
                                <div style={{ flex: 1 }}>
                                    <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>To SIP</label>
                                    <input type="text" value={rangeTo} onChange={e => setRangeTo(e.target.value)} placeholder="e.g. 8030"
                                        style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border-color)', borderRadius: 6, background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                                        onKeyDown={e => e.key === 'Enter' && selectRange()} />
                                </div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                                <button onClick={() => setShowRangeModal(false)} style={{ padding: '8px 16px', border: '1px solid var(--border-color)', borderRadius: 6, background: 'var(--bg-secondary)', color: 'var(--text-primary)', cursor: 'pointer' }}>Cancel</button>
                                <button onClick={selectRange} disabled={!rangeFrom || !rangeTo} style={{ padding: '8px 16px', border: 'none', borderRadius: 6, background: '#dc2626', color: '#fff', cursor: 'pointer', opacity: (!rangeFrom || !rangeTo) ? 0.5 : 1 }}>
                                    <CheckSquare size={14} style={{ marginRight: 4 }} /> Select Range
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Avatar Switch Modal */}
            {showAvatarModal && (
                <div className="bt-edit-modal-overlay" onClick={() => setShowAvatarModal(false)}>
                    <div className="bt-edit-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
                        <div className="bt-edit-modal-header">
                            <h3 style={{ margin: 0, fontSize: '1rem' }}>
                                <Users size={18} style={{ marginRight: 8 }} />
                                Switch Avatar ({selectedBots.size} bots selected)
                            </h3>
                            <button className="bt-edit-modal-close" onClick={() => setShowAvatarModal(false)}><X size={18} /></button>
                        </div>
                        <div className="bt-modal-body" style={{ padding: '16px 20px' }}>
                            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 16 }}>
                                Select an avatar to apply to the selected bots. Changes take effect on next call.
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {clientAvatars.map(av => (
                                    <button
                                        key={av.avatar_id}
                                        onClick={() => switchAvatar(av.avatar_id)}
                                        disabled={avatarSwitching}
                                        style={{
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            padding: '12px 16px', border: '1px solid var(--border-color)', borderRadius: 8,
                                            background: 'var(--bg-card)', cursor: 'pointer', textAlign: 'left',
                                            transition: 'all 0.15s'
                                        }}
                                        onMouseEnter={e => { e.target.style.borderColor = '#8b5cf6'; e.target.style.background = 'rgba(139,92,246,0.05)' }}
                                        onMouseLeave={e => { e.target.style.borderColor = 'var(--border-color)'; e.target.style.background = 'var(--bg-card)' }}
                                    >
                                        <div>
                                            <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                                                {av.avatar_name}
                                            </div>
                                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2, display: 'flex', gap: 8, alignItems: 'center' }}>
                                                {(() => {
                                                    const campaigns = ['Medicare', 'FE', 'Auto', 'ACA', 'Diabetic', 'MVA', 'Solar', 'Mortgage']
                                                    const detected = campaigns.find(c => av.avatar_name.toLowerCase().includes(c.toLowerCase()))
                                                    return detected ? <span style={{ padding: '1px 6px', borderRadius: 3, fontSize: '0.7rem', fontWeight: 600, background: 'rgba(139,92,246,0.15)', color: '#8b5cf6' }}>{detected}</span> : null
                                                })()}
                                                {av.label && <span>{av.label}</span>}
                                                {av.is_default === 1 && <span style={{ color: '#22c55e' }}>Default</span>}
                                            </div>
                                        </div>
                                        <ChevronDown size={14} style={{ transform: 'rotate(-90deg)', color: 'var(--text-muted)' }} />
                                    </button>
                                ))}
                                {clientAvatars.length === 0 && (
                                    <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 20 }}>
                                        No avatars assigned. Contact admin.
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
